import React, { useState, useEffect, useMemo } from 'react';
import { createUser, updateUser, getAllUsers, deleteUser, checkEmailExists, updateUserSectorRole } from '../services/UserService';
import { getSector, getSectors, Sector } from '../services/SectorService';
import SessionService from '../services/SessionService';
import { useNavigate } from 'react-router-dom';
import Toast from '../components/Toast';
import { ToastContainer } from '../components/Toast';
import { FiEdit2, FiTrash2, FiUserMinus, FiPlus, FiX, FiCheck, FiUser, FiMail, FiToggleRight, FiToggleLeft, FiChevronDown } from 'react-icons/fi';
import { useAuth } from '../hooks/useAuth';
import './AccessPage.css';

interface ApiResponse<T> {
  message: string;
  code: string;
  data: T[];
}

interface Person {
  id: number;
  name: string;
  email: string;
  phoneWhatsapp: string;
  avatarUrl: string;
  role: number | string; // 1/"Collaborator", 2/"Admin", 3/"SuperAdmin" - API pode retornar string
  status: boolean;
  sectors: Array<{
    id: number;
    name: string;
    roleNoSetor: number; // 1=Colaborador, 2=Admin, 3=SuperAdmin (dono)
  }>;
  invitedBy: number | null;
  invitedByName?: string | null;
  isDeleting?: boolean;
}

// Helper: verifica se role é Admin ou superior (API pode retornar número ou string)
const isAdminRole = (role: number | string): boolean => {
  if (typeof role === 'number') return role >= 2;
  const r = String(role).toLowerCase();
  return r === 'admin' || r === 'superadmin';
};

// Helper: retorna o label de exibição para o roleNoSetor (role do usuário naquele setor)
const getRoleNoSetorLabel = (roleNoSetor: number): string => {
  switch (roleNoSetor) {
    case 3: return 'SuperAdmin';
    case 2: return 'Administrador';
    default: return 'Colaborador';
  }
};

// Helper: converte role para número (para formulário)
const roleToNumber = (role: number | string): number => {
  if (typeof role === 'number') return role;
  const r = String(role).toLowerCase();
  if (r === 'superadmin') return 3;
  if (r === 'admin') return 2;
  return 1;
};

// Helper: determina o roleNoSetor efetivo do usuário nos setores compartilhados com o usuário logado
// Ex: Um SuperAdmin (role global 3) convidado como Colaborador (roleNoSetor 1) em um setor
//     deve mostrar "Colaborador" na tela de Acessos daquele setor
const getEffectiveRoleNoSetor = (
  person: { sectors: Array<{ id: number; name: string; roleNoSetor: number }> },
  currentUserSectors: Array<{ id: number }>
): number => {
  if (!person.sectors || person.sectors.length === 0) return 1;
  const currentSectorIds = currentUserSectors.map(s => s.id);
  const sharedSectors = person.sectors.filter(s => currentSectorIds.includes(s.id));
  if (sharedSectors.length === 0) return 1;
  return Math.max(...sharedSectors.map(s => s.roleNoSetor));
};

interface ToastMessage {
  id: number;
  message: string;
  type: 'success' | 'error' | 'info';
}

interface UpdateUserRequest {
  name: string;
  email: string;
  number: string;
  isActive: boolean;
  priority: 'low' | 'medium' | 'normal' | 'high';
  contactStatus: string;
  aiActive: boolean;
  sectorId: number;
}

const AccessPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin: isCurrentUserAdmin, userId, isLoading: authLoading } = useAuth();
  const [people, setPeople] = useState<Person[]>([]);
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [editingPerson, setEditingPerson] = useState<Person | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [newPerson, setNewPerson] = useState<Person>({
    id: 0,
    name: '',
    email: '',
    phoneWhatsapp: '',
    avatarUrl: '',
    role: 1, // Collaborator by default
    status: true,
    sectors: [],
    invitedBy: null
  });
  const [selectedSectors, setSelectedSectors] = useState<string[]>([]);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isAvatarLoading, setIsAvatarLoading] = useState<boolean>(true);
  const [roleValue, setRoleValue] = useState<number>(1); // 1=Collaborator, 2=Admin
  const [activeTab, setActiveTab] = useState<'users' | 'permissions'>('users');
  const [updatingRole, setUpdatingRole] = useState<string | null>(null); // "userId-sectorId" being updated


  useEffect(() => {
    fetchSectors();
  }, [navigate]);

  const [invitedBy, setInvitedBy] = useState<number | null>(userId);
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const [sectorSearchText, setSectorSearchText] = useState('');
  const [showSectorDropdown, setShowSectorDropdown] = useState(false);
  const [errors, setErrors] = useState<{
    name?: string;
    email?: string;
    sectors?: string;
  }>({});

  const [isSaving, setIsSaving] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState<number | null>(null);
  const [deleteModalPerson, setDeleteModalPerson] = useState<Person | null>(null);

  const [searchFilter, setSearchFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [tipoFilter, setTipoFilter] = useState<'all' | 'colaborador' | 'admin'>('all');
  const [convidadoPorFilter, setConvidadoPorFilter] = useState<'all' | 'me' | 'others'>('all');
  const [currentPage, setCurrentPage] = useState(0);
  const ITEMS_PER_PAGE = 10;

  // Verifica se o usuário logado é Admin ou SuperAdmin em pelo menos um setor
  const canManageAccess = useMemo(() => {
    return sectors.some(s => (s.roleNoSetor ?? 1) >= 2);
  }, [sectors]);


  const canEditUser = (person: Person): boolean => {
    if (!userId || person.id === userId) return false;
    const myAdminSectorIds = sectors.filter(s => (s.roleNoSetor ?? 1) >= 2).map(s => s.id);
    if (myAdminSectorIds.length === 0) return false;
    const sharedSectors = person.sectors?.filter(s => myAdminSectorIds.includes(s.id)) || [];
    return sharedSectors.length > 0;
  };

  const canDeleteUser = (person: Person): boolean => {
    if (!userId || person.id === userId) return false;
    const myAdminSectorIds = sectors.filter(s => (s.roleNoSetor ?? 1) >= 2).map(s => s.id);
    if (myAdminSectorIds.length === 0) return false;
    const removableSectors = person.sectors?.filter(s =>
      myAdminSectorIds.includes(s.id) && s.roleNoSetor !== 3
    ) || [];
    return removableSectors.length > 0;
  };

  // Estado para modal de usuário existente
  const [showExistingUserModal, setShowExistingUserModal] = useState(false);
  const [existingUserInfo, setExistingUserInfo] = useState<{
    userId: number;
    userName: string;
    userEmail: string;
  } | null>(null);

  const fetchSectors = async () => {
    try {
      setIsLoading(true);
      const token = SessionService.getToken();
      if (!token) {
        throw new Error('Token não encontrado');
      }
      const response = await getSectors(token);
      setSectors(response || []);
    } catch (error) {
      setSectors([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUsers = async (sectorsList: Sector[]) => {
    try {
      if (!userId) {
        throw new Error('ID do usuário não encontrado');
      }
      setIsLoading(true);
      // Busca todos os usuários, sem filtrar por quem convidou, para que usuários existentes associados apareçam
      const response = await getAllUsers(undefined, undefined);

      let usersData: Person[] = [];

      if (Array.isArray(response)) {
        usersData = response as unknown as Person[];
      } else if (response && 'data' in response) {
        usersData = (response as { data: Person[] }).data;
      }

      const processedUsers = usersData.map((user: any) => ({
        ...user,
        invitedByName: user.invitedByName ?? user.InvitedByName ?? null,
        isDeleting: false
      }));

      console.log('Usuários carregados:', processedUsers);
      setPeople(processedUsers);
    } catch (error: any) {
      // Se for 404 (não encontrou usuários), não é um erro
      if (error?.response?.status === 404 || error?.status === 404) {
        console.log('Nenhum usuário encontrado (404) - definindo lista vazia');
        setPeople([]);
      } else {
        console.error('Erro ao carregar usuários:', error);
        addToast('Erro ao carregar usuários', 'error');
        setPeople([]);
      }
    } finally {
      setIsLoading(false);
      setIsAvatarLoading(false);
    }
  };

  useEffect(() => {
    if (sectors.length > 0) {
      fetchUsers(sectors);
    }
  }, [sectors]);

  // Adicionar um log para monitorar o estado de carregamento e dados
  useEffect(() => {
  }, [isLoading, people, sectors]);

  const refreshUsers = async () => {
    try {
      setIsLoading(true);
      await fetchUsers(sectors);
    } catch (error) {
      setPeople([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditPerson = (person: Person) => {
    setEditingPerson(person);
    setSelectedSectors(person.sectors.map(sector => sector.id.toString()));
    setRoleValue(roleToNumber(person.role));
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setEditingPerson(null);
    setSelectedSectors([]);
    setSectorSearchText('');
    setShowSectorDropdown(false);
    setErrors({});
  };

  const handleOpenNewUser = () => {
    setEditingPerson(null);
    setNewPerson({ id: 0, name: '', email: '', phoneWhatsapp: '', avatarUrl: '', role: 1, status: true, sectors: [], invitedBy: null });
    setSelectedSectors([]);
    setRoleValue(1);
    setErrors({});
    setIsDrawerOpen(true);
  };

  const validateForm = () => {
    const newErrors: { name?: string; email?: string; sectors?: string; } = {};

    // Validação do nome
    if (!editingPerson?.name && !newPerson.name) {
      newErrors.name = 'O nome é obrigatório';
    } else if ((editingPerson?.name || newPerson.name).length < 3) {
      newErrors.name = 'O nome deve ter pelo menos 3 caracteres';
    }

    // Validação do email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!editingPerson?.email && !newPerson.email) {
      newErrors.email = 'O email é obrigatório';
    } else if (!emailRegex.test(editingPerson?.email || newPerson.email)) {
      newErrors.email = 'Digite um email válido';
    }

    // Validação de setores (Obrigatório)
    if (selectedSectors.length === 0) {
      newErrors.sectors = 'Selecione pelo menos um setor';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleToggleStatus = () => {
    if (editingPerson) {
      setEditingPerson(prev => ({
        ...prev!,
        status: !prev!.status
      }));
    } else {
      setNewPerson(prev => ({
        ...prev,
        status: !prev.status
      }));
    }
  };

  const handleSavePerson = async () => {
    if (!validateForm()) {
      return;
    }
    if (editingPerson) {
      try {
        setIsSaving(true);
        const sectorsToUpdate = selectedSectors.map((sectorId) => {
          return {
            Id: Number(sectorId),
            IsShared: true,
          };
        });
        const updatedUser = {
          name: editingPerson.name,
          email: editingPerson.email,
          phoneWhatsapp: editingPerson.phoneWhatsapp,
          status: editingPerson.status,
          role: roleToNumber(editingPerson.role),
          sectors: sectorsToUpdate,
          invitedBy: editingPerson.invitedBy
        };

        console.log('Salvando usuário com dados:', updatedUser);
        const result = await updateUser(editingPerson.id, updatedUser as any);
        console.log('Resultado da atualização:', result);
        addToast('Usuário atualizado com sucesso!', 'success');
        handleCloseDrawer();
        await refreshUsers();
      } catch (error) {
        console.error('Erro ao atualizar usuário:', error);
        addToast('Erro ao atualizar usuário', 'error');
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleDeletePerson = async (personId: string) => {
    if (!deleteModalPerson) return;
    try {
      setDeletingUserId(Number(personId));
      await deleteUser(personId);
      addToast('Usuário removido dos seus setores com sucesso!', 'success');
      closeDeleteModal();
      await refreshUsers();
    } catch (error) {
      addToast('Erro ao remover usuário', 'error');
    } finally {
      setDeletingUserId(null);
    }
  };

  const openDeleteModal = (person: Person) => {
    setDeleteModalPerson(person);
  };

  const closeDeleteModal = () => {
    setDeleteModalPerson(null);
  };

  const handleAddPerson = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setIsSaving(true);

      // Primeiro, verificar se o email já existe
      const emailCheck = await checkEmailExists(newPerson.email);

      if (emailCheck.exists && emailCheck.userId) {
        // Verificar se o usuário já está nos setores selecionados
        const existingPerson = people.find(p => p.id === emailCheck.userId);
        if (existingPerson?.sectors) {
          const existingSectorIds = existingPerson.sectors.map(s => s.id.toString());

          // Verificar se é SuperAdmin em algum setor selecionado — bloquear
          const isSuperAdminInSelected = existingPerson.sectors.some(
            s => selectedSectors.includes(s.id.toString()) && s.roleNoSetor === 3
          );
          if (isSuperAdminInSelected) {
            addToast('Este usuário é o dono (SuperAdmin) de um dos setores selecionados. Não é possível convidá-lo.', 'error');
            setIsSaving(false);
            return;
          }

          // Filtrar setores onde já está presente
          const alreadyInSectors = selectedSectors.filter(sid => existingSectorIds.includes(sid));
          if (alreadyInSectors.length > 0) {
            if (alreadyInSectors.length === selectedSectors.length) {
              addToast('Este usuário já está em todos os setores selecionados.', 'error');
              setIsSaving(false);
              return;
            }
            // Remover setores duplicados e avisar
            const newSectors = selectedSectors.filter(sid => !existingSectorIds.includes(sid));
            setSelectedSectors(newSectors);
            addToast(`Usuário já presente em ${alreadyInSectors.length} setor(es). Apenas os novos serão adicionados.`, 'info');
          }
        }

        // Email existe, mostrar modal de confirmação
        setExistingUserInfo({
          userId: emailCheck.userId,
          userName: emailCheck.userName || newPerson.email,
          userEmail: emailCheck.userEmail || newPerson.email,
        });
        setShowExistingUserModal(true);
        setIsSaving(false);
        return;
      }

      // Email não existe, criar normalmente
      await createNewUser();
    } catch (error) {
      addToast('Erro ao verificar usuário', 'error');
      setIsSaving(false);
    }
  };

  // Função para criar novo usuário (usada quando email não existe)
  const createNewUser = async () => {
    try {
      setIsSaving(true);
      const sectorsToUpdate = selectedSectors.map((sectorId) => {
        return {
          Id: Number(sectorId),
          IsShared: true,
        };
      });
      const newUser: any = {
        name: newPerson.name,
        email: newPerson.email,
        avatarUrl: '',
        phoneWhatsapp: '',
        role: roleValue,
        status: newPerson.status,
        sectors: sectorsToUpdate,
        invitedBy: invitedBy,
      };
      await createUser(newUser);
      addToast('Usuário criado com sucesso!', 'success');
      resetForm();
      await refreshUsers();
    } catch (error) {
      addToast('Erro ao criar usuário', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // Função para adicionar usuário existente aos setores (quando confirma no modal)
  const handleAddExistingUser = async () => {
    if (!existingUserInfo) return;

    if (selectedSectors.length === 0) {
      addToast('Selecione pelo menos um setor para adicionar o usuário existente', 'error');
      return;
    }

    try {
      setIsSaving(true);
      const sectorsToUpdate = selectedSectors.map((sectorId) => {
        return {
          Id: Number(sectorId),
          IsShared: true,
        };
      });

      // Usar createUser que vai "associar" o usuário existente
      const userData: any = {
        name: existingUserInfo.userName,
        email: existingUserInfo.userEmail,
        avatarUrl: '',
        phoneWhatsapp: '',
        role: roleValue,
        status: newPerson.status,
        sectors: sectorsToUpdate,
        invitedBy: invitedBy,
      };

      await createUser(userData);
      addToast('Usuário adicionado aos seus setores com sucesso!', 'success');
      setShowExistingUserModal(false);
      setExistingUserInfo(null);
      resetForm();
      await refreshUsers();
    } catch (error) {
      addToast('Erro ao adicionar usuário aos setores', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // Função para resetar o formulário
  const resetForm = () => {
    setIsDrawerOpen(false);
    setNewPerson({
      id: 0,
      name: '',
      email: '',
      phoneWhatsapp: '',
      avatarUrl: '',
      role: 1,
      status: true,
      sectors: [],
      invitedBy: null
    });
    setSelectedSectors([]);
    setRoleValue(1);
  };

  const addToast = (message: string, type: 'success' | 'error' | 'info') => {
    const newToast = {
      id: Date.now(),
      message,
      type,
    };
    setToasts(current => [...current, newToast]);
  };

  const removeToast = (id: number) => {
    setToasts(current => current.filter(toast => toast.id !== id));
  };

  const filteredPeople = useMemo(() => {
    const mySectorIds = sectors.map(s => s.id);
    return people.filter(p => {
      // Base: só mostrar se compartilha setor comigo OU se eu convidei
      if (p.id === userId) return false; // Não mostrar a mim mesmo
      const sharesAnySector = p.sectors?.some(s => mySectorIds.includes(s.id)) ?? false;
      const invitedByMe = p.invitedBy === userId;
      if (!sharesAnySector && !invitedByMe) return false;

      const matchSearch = !searchFilter.trim() ||
        p.name.toLowerCase().includes(searchFilter.toLowerCase()) ||
        (p.email || '').toLowerCase().includes(searchFilter.toLowerCase()) ||
        (p.invitedByName || '').toLowerCase().includes(searchFilter.toLowerCase());
      const matchStatus = statusFilter === 'all' ||
        (statusFilter === 'active' && p.status) ||
        (statusFilter === 'inactive' && !p.status);
      // Determinar o roleNoSetor efetivo: pegar o maior roleNoSetor dentre os setores compartilhados
      const effectiveRoleNoSetor = getEffectiveRoleNoSetor(p, sectors);
      const matchTipo = tipoFilter === 'all' ||
        (tipoFilter === 'colaborador' && effectiveRoleNoSetor < 2) ||
        (tipoFilter === 'admin' && effectiveRoleNoSetor >= 2);
      const matchConvidadoPor = convidadoPorFilter === 'all' ||
        (convidadoPorFilter === 'me' && p.invitedBy === userId) ||
        (convidadoPorFilter === 'others' && p.invitedBy != null && p.invitedBy !== userId);
      return matchSearch && matchStatus && matchTipo && matchConvidadoPor;
    });
  }, [people, searchFilter, statusFilter, tipoFilter, convidadoPorFilter, userId, sectors]);

  const paginatedPeople = useMemo(() => {
    const start = currentPage * ITEMS_PER_PAGE;
    return filteredPeople.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredPeople, currentPage]);

  const totalPages = Math.ceil(filteredPeople.length / ITEMS_PER_PAGE);

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    const newRoleValue = value === 'Administrador' ? 2 : 1;

    console.log('Mudando nível de acesso para:', value, 'role:', newRoleValue);

    if (editingPerson) {
      setEditingPerson(prev => {
        const updated = {
          ...prev!,
          role: newRoleValue
        };
        console.log('Atualizando editingPerson:', updated);
        return updated;
      });
    } else {
      setRoleValue(newRoleValue);
    }
  };

  const handleMultiSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const options = e.target.options;
    const selectedValues: string[] = [];
    for (let i = 0; i < options.length; i++) {
      if (options[i].selected) {
        selectedValues.push(options[i].value);
      }
    }
    setSelectedSectors(selectedValues);
  };

  const isSectorLockedForEdit = (sectorId: number): boolean => {
    if (!editingPerson) return false;
    return editingPerson.sectors?.some(s => s.id === sectorId && s.roleNoSetor === 3) ?? false;
  };

  const handleSectorSelect = (sectorId: number) => {
    const sectorIdStr = sectorId.toString();
    if (selectedSectors.includes(sectorIdStr) && isSectorLockedForEdit(sectorId)) {
      addToast('Não é possível remover um SuperAdmin do setor.', 'error');
      return;
    }
    if (selectedSectors.includes(sectorIdStr)) {
      setSelectedSectors(selectedSectors.filter(id => id !== sectorIdStr));
    } else {
      setSelectedSectors([...selectedSectors, sectorIdStr]);
    }
  };

  const handleRemoveSector = (sectorId: string) => {
    if (isSectorLockedForEdit(Number(sectorId))) {
      addToast('Não é possível remover um SuperAdmin do setor.', 'error');
      return;
    }
    setSelectedSectors(selectedSectors.filter(id => id !== sectorId));
  };

  // Handler para alterar role por setor
  const handleUpdateSectorRole = async (targetUserId: number, sectorId: number, newRole: number) => {
    const key = `${targetUserId}-${sectorId}`;
    setUpdatingRole(key);
    try {
      await updateUserSectorRole(targetUserId, sectorId, newRole);
      addToast('Permissão atualizada com sucesso!', 'success');
      await refreshUsers();
    } catch (error: any) {
      const msg = error?.response?.data?.Message || error?.response?.data?.message || 'Erro ao atualizar permissão';
      addToast(msg, 'error');
    } finally {
      setUpdatingRole(null);
    }
  };

  // Determina se o usuário logado pode editar o role de um target em um setor
  const canEditRoleInSector = (targetPerson: Person, sectorId: number): boolean => {
    if (!userId) return false;
    // Não pode editar a si mesmo
    if (targetPerson.id === userId) return false;
    // Buscar meu role neste setor
    const mySector = sectors.find(s => s.id === sectorId);
    if (!mySector || (mySector.roleNoSetor ?? 1) < 2) return false;
    // Buscar role do alvo neste setor
    const targetSector = targetPerson.sectors?.find(s => s.id === sectorId);
    if (!targetSector) return false;
    // Não pode editar SuperAdmin (dono)
    if (targetSector.roleNoSetor === 3) return false;
    // Não pode editar quem me convidou (precisaria info do backend, por hora protegemos via API)
    return true;
  };

  // Gera flat list de usuário+setor para a aba de permissões
  const userSectorPermissions = useMemo(() => {
    const result: Array<{
      person: Person;
      sectorId: number;
      sectorName: string;
      roleNoSetor: number;
      canEdit: boolean;
    }> = [];
    const currentSectorIds = sectors.map(s => s.id);
    people.forEach(person => {
      if (!person.sectors) return;
      person.sectors
        .filter(s => currentSectorIds.includes(s.id))
        .forEach(s => {
          result.push({
            person,
            sectorId: s.id,
            sectorName: s.name,
            roleNoSetor: s.roleNoSetor,
            canEdit: canEditRoleInSector(person, s.id),
          });
        });
    });
    return result;
  }, [people, sectors, userId]);

  // Filtros para aba de permissões
  const [permSearchFilter, setPermSearchFilter] = useState('');
  const [permSectorFilter, setPermSectorFilter] = useState<string>('all');

  const filteredPermissions = useMemo(() => {
    return userSectorPermissions.filter(item => {
      if (permSearchFilter) {
        const s = permSearchFilter.toLowerCase();
        if (!item.person.name.toLowerCase().includes(s) && !item.person.email.toLowerCase().includes(s)) return false;
      }
      if (permSectorFilter !== 'all' && item.sectorId.toString() !== permSectorFilter) return false;
      return true;
    });
  }, [userSectorPermissions, permSearchFilter, permSectorFilter]);

  return (
    <div className="ap-screen">
      <div className="ap-header">
        <div className="ap-header-content">
          <h1>Acessos</h1>
          <p className="ap-header-description">Gerencie os acessos dos usuários</p>
        </div>
        {canManageAccess && activeTab === 'users' && (
          <button
            className="ap-add-button"
            onClick={handleOpenNewUser}
          >
            <FiPlus /> Novo Usuário
          </button>
        )}
      </div>

      {/* Sub-tabs */}
      <div className="ap-tabs">
        <button
          className={`ap-tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          Usuários
        </button>
        <button
          className={`ap-tab ${activeTab === 'permissions' ? 'active' : ''}`}
          onClick={() => setActiveTab('permissions')}
        >
          Permissões por Setor
        </button>
      </div>

      {isLoading ? (
        <div className="ap-content" style={{ position: 'relative', flex: '1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="loading-overlay">
            <div className="card-loading">
              <div className="card-loading-spinner" />
              <span className="loading-text">Carregando acessos...</span>
            </div>
          </div>
        </div>
      ) : activeTab === 'permissions' ? (
        <div className="ap-content">
          <div className="ap-perm-description">
            <p>Gerencie as permissões de cada usuário em cada setor individualmente. Altere entre <strong>Colaborador</strong> e <strong>Administrador</strong> conforme necessário.</p>
          </div>
          <div className="ap-filters">
            <input
              type="text"
              className="ap-filter-search"
              placeholder="Buscar por nome ou email..."
              value={permSearchFilter}
              onChange={(e) => setPermSearchFilter(e.target.value)}
            />
            <select
              className="ap-filter-select"
              value={permSectorFilter}
              onChange={(e) => setPermSectorFilter(e.target.value)}
            >
              <option value="all">Setor: Todos</option>
              {sectors.map(s => (
                <option key={s.id} value={s.id.toString()}>{s.name}</option>
              ))}
            </select>
          </div>
          {filteredPermissions.length === 0 ? (
            <div className="ap-empty">
              <p>Nenhuma associação encontrada</p>
            </div>
          ) : (
            <div className="ap-list-container">
              <table className="ap-list-table">
                <thead>
                  <tr>
                    <th>Usuário</th>
                    <th>Email</th>
                    <th>Setor</th>
                    <th>Permissão no Setor</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPermissions.map(item => {
                    const key = `${item.person.id}-${item.sectorId}`;
                    const isUpdating = updatingRole === key;
                    return (
                      <tr key={key}>
                        <td><span className="ap-list-name">{item.person.name}</span></td>
                        <td>
                          <span className="ap-list-email">
                            <FiMail className="ap-list-icon" />
                            {item.person.email || '—'}
                          </span>
                        </td>
                        <td><span className="ap-sector-tag">{item.sectorName}</span></td>
                        <td>
                          {item.roleNoSetor === 3 ? (
                            <span className="ap-role-badge ap-role-superadmin" title="Dono do setor — não pode ser alterado">
                              SuperAdmin (dono)
                            </span>
                          ) : item.canEdit ? (
                            <div className="ap-role-select-wrapper">
                              <select
                                className="ap-role-select"
                                value={item.roleNoSetor}
                                disabled={isUpdating}
                                onChange={(e) => handleUpdateSectorRole(item.person.id, item.sectorId, Number(e.target.value))}
                              >
                                <option value={1}>Colaborador</option>
                                <option value={2}>Administrador</option>
                              </select>
                              {isUpdating && <span className="ap-spinner-btn ap-spinner-inline" />}
                            </div>
                          ) : (
                            <span className="ap-role-badge" title="Sem permissão para alterar">
                              {getRoleNoSetorLabel(item.roleNoSetor)}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        <div className="ap-content">
          {people.length === 0 ? (
            <div className="ap-empty">
              <p>Nenhum usuário encontrado</p>
            </div>
          ) : (
            <>
              <div className="ap-filters">
                <input
                  type="text"
                  className="ap-filter-search"
                  placeholder="Buscar por nome ou email..."
                  value={searchFilter}
                  onChange={(e) => { setSearchFilter(e.target.value); setCurrentPage(0); }}
                />
                <select
                  className="ap-filter-select"
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value as 'all' | 'active' | 'inactive'); setCurrentPage(0); }}
                >
                  <option value="all">Status: Todos</option>
                  <option value="active">Ativo</option>
                  <option value="inactive">Inativo</option>
                </select>
                <select
                  className="ap-filter-select"
                  value={tipoFilter}
                  onChange={(e) => { setTipoFilter(e.target.value as 'all' | 'colaborador' | 'admin'); setCurrentPage(0); }}
                >
                  <option value="all">Tipo: Todos</option>
                  <option value="colaborador">Colaborador</option>
                  <option value="admin">Administrador</option>
                </select>
                <select
                  className="ap-filter-select"
                  value={convidadoPorFilter}
                  onChange={(e) => { setConvidadoPorFilter(e.target.value as 'all' | 'me' | 'others'); setCurrentPage(0); }}
                >
                  <option value="all">Convidado por: Todos</option>
                  <option value="me">Convidado por mim</option>
                  <option value="others">Convidado por outros</option>
                </select>
              </div>
              {filteredPeople.length > 0 ? (
                <div className="ap-list-container">
                  <table className="ap-list-table">
                    <thead>
                      <tr>
                        <th>Status</th>
                        <th>Nome</th>
                        <th>Email</th>
                        <th>Tipo</th>
                        <th>Convidado por</th>
                        <th>Setores</th>
                        {canManageAccess && <th className="ap-list-actions-col">Ações</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedPeople.map((person) => (
                        <tr key={person.id}>
                          <td>
                            <div className={`ap-status-indicator ap-status-indicator-inline ${person.status ? 'active' : 'inactive'}`}>
                              {person.status ? <><FiToggleRight /><span>Ativo</span></> : <><FiToggleLeft /><span>Desativado</span></>}
                            </div>
                          </td>
                          <td><span className="ap-list-name">{person.name}</span></td>
                          <td>
                            <span className="ap-list-email">
                              <FiMail className="ap-list-icon" />
                              {person.email || '—'}
                            </span>
                          </td>
                          <td>
                            <span className={`ap-role-badge ${getEffectiveRoleNoSetor(person, sectors) === 3 ? 'ap-role-superadmin' : getEffectiveRoleNoSetor(person, sectors) === 2 ? 'ap-role-admin' : ''}`}>
                              {getRoleNoSetorLabel(getEffectiveRoleNoSetor(person, sectors))}
                            </span>
                          </td>
                          <td>
                            <span className="ap-info-text">
                              {person.invitedByName || '—'}
                            </span>
                          </td>
                          <td>
                            {(() => {
                              const userSectorIds = sectors.map(s => s.id);
                              const commonSectors = person.sectors
                                ? person.sectors.filter(s => userSectorIds.includes(s.id))
                                : [];
                              return commonSectors.length > 0 ? (
                                <div className="ap-info-sectors">
                                  {commonSectors.map(s => (
                                    <span key={s.id} className="ap-sector-tag">{s.name}</span>
                                  ))}
                                </div>
                              ) : (
                                <span className="ap-info-text">—</span>
                              );
                            })()}
                          </td>
                          {canManageAccess && canEditUser(person) && (
                            <td className="ap-list-actions-cell">
                              <div className="ap-card-actions">
                                <button className="ap-action-button edit" onClick={() => handleEditPerson(person)} title="Editar">
                                  <FiEdit2 />
                                </button>
                                {canDeleteUser(person) && (
                                  <button className="ap-action-button delete" onClick={() => openDeleteModal(person)} title="Remover acesso aos meus setores">
                                    <FiUserMinus />
                                  </button>
                                )}
                              </div>
                            </td>
                          )}
                          {canManageAccess && !canEditUser(person) && (
                            <td className="ap-list-actions-cell"></td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="ap-empty ap-empty-filtered">
                  <p>Nenhum usuário encontrado com os filtros aplicados</p>
                </div>
              )}
              {filteredPeople.length > 0 && totalPages > 1 && (
                <div className="ap-pagination">
                  <button
                    className="ap-pagination-btn"
                    onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                    disabled={currentPage === 0}
                  >
                    Anterior
                  </button>
                  <span className="ap-pagination-info">
                    Página {currentPage + 1} de {totalPages} ({filteredPeople.length} resultado{filteredPeople.length !== 1 ? 's' : ''})
                  </span>
                  <button
                    className="ap-pagination-btn"
                    onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                    disabled={currentPage >= totalPages - 1}
                  >
                    Próxima
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {(isDrawerOpen || editingPerson) && (
        <div className="ap-drawer-overlay">
          <div className="ap-drawer">
            <div className="ap-drawer-header">
              <h2 className="ap-drawer-title">{editingPerson ? 'Editar Usuário' : 'Novo Usuário'}</h2>
              <button
                className="ap-button ap-button-icon"
                onClick={handleCloseDrawer}
              >
                <FiX />
              </button>
            </div>

            <div className="ap-drawer-content">
              <form className="ap-form" onSubmit={(e) => {
                e.preventDefault();
                editingPerson ? handleSavePerson() : handleAddPerson();
              }}>
                <div className="ap-form-group">
                  <label className="ap-form-label">Nome do Usuário <span className="ap-required">*</span></label>
                  <input
                    type="text"
                    className={`ap-form-input ${errors.name ? 'error' : ''}`}
                    placeholder="Digite o nome do usuário"
                    value={editingPerson?.name || newPerson.name}
                    onChange={(e) => {
                      if (editingPerson) {
                        setEditingPerson({ ...editingPerson, name: e.target.value });
                      } else {
                        setNewPerson({ ...newPerson, name: e.target.value });
                      }
                      if (errors.name) {
                        setErrors({ ...errors, name: undefined });
                      }
                    }}
                    disabled={isLoading || isSaving}
                  />
                  {errors.name && <span className="ap-error-message">{errors.name}</span>}
                </div>

                <div className="ap-form-group">
                  <label className="ap-form-label">Email <span className="ap-required">*</span></label>
                  <input
                    type="email"
                    className={`ap-form-input ${errors.email ? 'error' : ''}`}
                    placeholder="Digite o email"
                    value={editingPerson?.email || newPerson.email}
                    onChange={(e) => {
                      if (editingPerson) {
                        setEditingPerson({ ...editingPerson, email: e.target.value });
                      } else {
                        setNewPerson({ ...newPerson, email: e.target.value });
                      }
                      if (errors.email) {
                        setErrors({ ...errors, email: undefined });
                      }
                    }}
                    disabled={isLoading || isSaving}
                  />
                  {errors.email && <span className="ap-error-message">{errors.email}</span>}
                </div>

                {!editingPerson && (
                  <div className="ap-form-group">
                    <label className="ap-form-label">Nível de Acesso Inicial</label>
                    <select
                      className="ap-form-select"
                      value={roleValue >= 2 ? "Administrador" : "Colaborador"}
                      onChange={handleSelectChange}
                      disabled={isLoading || isSaving}
                    >
                      <option value="Colaborador">Colaborador</option>
                      <option value="Administrador">Administrador</option>
                    </select>
                    <span className="ap-form-hint">Este nível será aplicado a todos os setores selecionados. Depois, você pode ajustar individualmente na aba "Permissões por Setor".</span>
                  </div>
                )}

                <div className="ap-form-group">
                  <label className="ap-form-label">Status</label>
                  <div className="ap-switch">
                    <div className={`ap-switch-label ${editingPerson ? (editingPerson.status ? 'active' : 'inactive') : (newPerson.status ? 'active' : 'inactive')}`}>
                      <span className="ap-switch-text">
                        {editingPerson ? (editingPerson.status ? 'Ativo' : 'Inativo') : (newPerson.status ? 'Ativo' : 'Inativo')}
                      </span>
                    </div>
                    <div
                      className={`ap-switch-toggle ${editingPerson ? (editingPerson.status ? 'active' : '') : (newPerson.status ? 'active' : '')}`}
                      onClick={handleToggleStatus}
                      role="button"
                      tabIndex={0}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleToggleStatus();
                        }
                      }}
                    />
                  </div>
                </div>

                <div className="ap-form-group">
                  <label className="ap-form-label">Setores <span className="ap-required">*</span></label>
                  <div
                    className={`ap-multiselect${showSectorDropdown ? ' open' : ''}`}
                    onClick={() => setShowSectorDropdown(prev => !prev)}
                  >
                    <div className="ap-multiselect-content">
                      {selectedSectors.length === 0 ? (
                        <div className="ap-multiselect-placeholder">Selecione os setores</div>
                      ) : (
                        <div className="ap-multiselect-tags">
                          {selectedSectors.map(sectorId => {
                            const sector = sectors.find(s => s.id.toString() === sectorId);
                            if (!sector) return null;
                            return (
                              <span key={sector.id} className="ap-multiselect-tag">
                                {sector.name}
                                {!isSectorLockedForEdit(Number(sectorId)) ? (
                                  <button
                                    type="button"
                                    className="ap-multiselect-tag-remove"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemoveSector(sectorId);
                                    }}
                                  >
                                    <FiX size={14} />
                                  </button>
                                ) : (
                                  <span className="ap-multiselect-tag-lock" title="SuperAdmin — não pode ser removido">🔒</span>
                                )}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <div className="ap-multiselect-toggle">
                      <FiChevronDown size={18} />
                    </div>

                    {showSectorDropdown && (
                      <div className="ap-multiselect-dropdown">
                        <div className="ap-multiselect-search">
                          <input
                            type="text"
                            placeholder="Buscar setores..."
                            value={sectorSearchText}
                            onChange={(e) => setSectorSearchText(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        <div className="ap-multiselect-options">
                          {sectors
                            .filter(sector =>
                              sector.name.toLowerCase().includes(sectorSearchText.toLowerCase())
                            )
                            .map(sector => (
                              <div
                                key={sector.id}
                                className={`ap-multiselect-option${selectedSectors.includes(sector.id.toString()) ? ' selected' : ''}${isSectorLockedForEdit(sector.id) ? ' locked' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSectorSelect(sector.id);
                                }}
                              >
                                <div className="ap-multiselect-checkbox" />
                                {sector.name}
                                {isSectorLockedForEdit(sector.id) && <span className="ap-sector-lock-label"> (dono)</span>}
                              </div>
                            ))}
                          {sectors.filter(sector =>
                            sector.name.toLowerCase().includes(sectorSearchText.toLowerCase())
                          ).length === 0 && (
                              <div className="ap-multiselect-no-results">
                                Nenhum setor encontrado
                              </div>
                            )}
                        </div>
                      </div>
                    )}
                  </div>
                  {errors.sectors && <span className="ap-error-message">{errors.sectors}</span>}
                </div>

                <div className="ap-drawer-footer">
                  <button
                    type="button"
                    className="ap-button ap-button-secondary"
                    onClick={handleCloseDrawer}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="ap-button ap-button-primary"
                    disabled={isLoading || isSaving}
                  >
                    {isSaving && <span className="ap-spinner-btn" />}
                    {editingPerson ? 'Salvar' : 'Adicionar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmação para remover acesso */}
      {deleteModalPerson && (
        <div className="ap-modal-overlay" onClick={closeDeleteModal}>
          <div className="ap-modal ap-modal-delete" onClick={e => e.stopPropagation()}>
            <div className="ap-modal-header">
              <h2 className="ap-modal-title">Remover Acesso</h2>
              <button className="ap-modal-close" onClick={closeDeleteModal}>
                <FiX />
              </button>
            </div>
            <div className="ap-modal-content">
              <div className="ap-modal-icon ap-modal-icon-danger">
                <FiUserMinus />
              </div>
              <h3 className="ap-modal-person-name">{deleteModalPerson.name}</h3>
              <p className="ap-modal-person-email">{deleteModalPerson.email}</p>
              <p className="ap-modal-text">
                Tem certeza que deseja remover o acesso deste usuário dos seus setores?
                Ele perderá acesso aos setores que você gerencia (Admin/SuperAdmin).
                Setores onde ele é SuperAdmin (dono) não serão afetados.
              </p>
            </div>
            <div className="ap-modal-footer">
              <button
                type="button"
                className="ap-button ap-button-secondary"
                onClick={closeDeleteModal}
                disabled={!!deletingUserId}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="ap-button ap-button-danger"
                onClick={() => handleDeletePerson(deleteModalPerson.id.toString())}
                disabled={!!deletingUserId}
              >
                {deletingUserId === deleteModalPerson.id && <span className="ap-spinner-btn" />}
                <FiUserMinus /> Remover Acesso
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmação para usuário existente */}
      {showExistingUserModal && existingUserInfo && (
        <div className="ap-drawer-overlay">
          <div className="ap-drawer" style={{ maxWidth: '450px' }}>
            <div className="ap-drawer-header">
              <h2 className="ap-drawer-title">Usuário já existe</h2>
              <button
                className="ap-button ap-button-icon"
                onClick={() => {
                  setShowExistingUserModal(false);
                  setExistingUserInfo(null);
                }}
              >
                <FiX />
              </button>
            </div>

            <div className="ap-drawer-content">
              <div style={{ textAlign: 'center', padding: '24px 0' }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  fontSize: '24px',
                  color: '#fff'
                }}>
                  <FiUser />
                </div>
                <h3 style={{ margin: '0 0 8px', color: '#e2e8f0' }}>
                  {existingUserInfo.userName}
                </h3>
                <p style={{ margin: '0 0 16px', color: '#94a3b8', fontSize: '14px' }}>
                  {existingUserInfo.userEmail}
                </p>
                <p style={{ margin: '0', color: '#e2e8f0', lineHeight: '1.6' }}>
                  Este usuário já está cadastrado no sistema.
                  <br />
                  Deseja adicioná-lo aos seus setores selecionados?
                </p>
              </div>

              <div className="ap-drawer-footer" style={{ justifyContent: 'center', gap: '12px' }}>
                <button
                  type="button"
                  className="ap-button ap-button-secondary"
                  onClick={() => {
                    setShowExistingUserModal(false);
                    setExistingUserInfo(null);
                  }}
                  disabled={isSaving}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="ap-button ap-button-primary"
                  onClick={handleAddExistingUser}
                  disabled={isSaving}
                >
                  {isSaving && <span className="ap-spinner-btn" />}
                  Sim, adicionar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer>
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </ToastContainer>
    </div>
  );
};

export default AccessPage;
